import api from '@shop/frontend/web/js/api';
import { modal } from '@shop/frontend/web/js/components/Modal';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { readonly, ref, computed } from 'vue';

// state
/** @type {{ value: import("@api/types/cart").Order[] }} */
const orders = ref([]);
const history = ref([]);
const isShowFastBuyModal = ref(false);
const eventTypeToPay = ref();
const currentOpenedEventId = ref(null);

// mutations
/**
 * @param {boolean} status
 */
function setFastBuyModalStatus(status) {
  isShowFastBuyModal.value = status;
}

/**
 * @param {string} eventType
 */
function setEventTypeToPay(eventType) {
  eventTypeToPay.value = eventType;
}

/**
 * @param {number} eventId
 */
function setCurrentOpenedEventId(eventId) {
  currentOpenedEventId.value = eventId;
}

/**
 * @param {number} orderId
 * @param {number} eventId
 * @param {import("@api/types/cart").Ticket} removedTicket
 */
function removeTicketMutation(orderId, eventId, removedTicket) {
  const orderKey = orders.value.findIndex((order) => order.id === orderId);
  const eventIndex = orders.value[orderKey].cases.findIndex((event) => event.id === eventId);
  orders.value[orderKey].cases[eventIndex].tickets = orders.value[orderKey].cases[
    eventIndex
  ].tickets.filter((ticket) => ticket.uid !== removedTicket.uid);
}

/**
 * Обновляет цены заказа и проверяет пустые элементы
 * @param {Object} cartRes
 * @param {number} cartRes.summ
 * @param {Object.<string, import("@api/types/cart").ActiveCartsEvent} cartRes.active_carts
 */
function updateOrderPrices(cartRes) {
  const orderKey = orders.value.findIndex((order) => order.id === cartRes.order_id);
  const editedOrder = orders.value[orderKey];
  editedOrder.summaryPrice = cartRes.summ;
  editedOrder.cases.forEach((event, index) => {
    /* Если в мероприятии нет билетов то удаляет */
    if (!cartRes.active_carts[event.id]) return editedOrder.cases.splice(index, 1);
    /* Проходит по каждому билету */
    cartRes.active_carts[event.id].carts.forEach((activeTicket) => {
      const ticketIndex = editedOrder.cases[index].tickets.findIndex(
        (ticket) => ticket.id === activeTicket.id,
      );
      if (ticketIndex >= 0) {
        editedOrder.cases[index].tickets[ticketIndex].price = activeTicket.price;
      }
    });

    /* Обновляет общую сумму мероприятия */
    editedOrder.cases[index].summaryPrice = cartRes.active_carts[event.id].summ;
  });

  /* удаляет заказ если нет мероприятии */
  if (!(editedOrder.cases.length > 0)) orders.value.splice(orderKey, 1);
}

/**
 * Обновляет скидку у заказа
 * @param {number} orderId
 * @param {number} newSale
 */
function updateOrderSale(orderId, newSale) {
  const currentOrder = getOrderById(orderId);
  currentOrder.sale = newSale;
}

/**
 * Удаляет заказ по его id
 * @param {number} orderId
 */
function deleteOrderByIdFromStore(orderId) {
  const ticketsCount = getTicketCountByOrderId(orderId);

  // TODO: удалить после переноса хедера на Vue
  window.CartService.count = window.CartService.count - ticketsCount;
  window.CartService.updateCount();

  orders.value = orders.value.filter((order) => order.id !== orderId);
}

// actions
/**
 * @param {Object} params
*/
function loadHistory(params = {}) {
  return api.orders.getHistory(params).then((res) => (history.value = res));
}

function loadCart() {
  return api.orders.getCart().then((res) => (orders.value = res));
}

function clearCart() {
  api.order
    .cancelAll()
    .then((res) => {
      if (!res.result) throw new Error(res.message);
      orders.value = [];
      // TODO: удалить после переноса хедера на Vue
      window.CartService.count = 0;
      window.CartService.updateCount();
    })
    .catch((error = {}) => {
      modal.error(
        error.message
        ?? translator.t('main', 'An error occurred when deleting from the basket!'),
      );
    });
}

function updateSummaryPrice(summaryPrice, orderId) {
  const orderKey = orders.value.findIndex((order) => order.id === orderId);
  const editedOrder = orders.value[orderKey];
  editedOrder.summaryPrice = summaryPrice;
}

function removeEvent(eventId, orderId) {
  api.eventCart
    .removeEventFromOrder({ eventId, orderId })
    .then((res) => {
      if (res.error) throw new Error(res.msg);
      if (res.refresh) return window.location.reload();
      if (!res.success) throw new Error();

      const orderKey = orders.value.findIndex((order) => order.id === orderId);
      orders.value[orderKey].cases = orders.value[orderKey].cases.filter(
        (event) => event.id !== eventId,
      );
      /* удаляет заказ если нет мероприятии */
      if (!orders.value[orderKey].cases.length) {
        orders.value.splice(orderKey, 1);
      } else {
        let summaryPrice = 0;
        orders.value[orderKey].cases.map((eventItem) => summaryPrice += eventItem.summaryPrice);
        updateSummaryPrice(summaryPrice, orderId);
      }

      window.CartService.count = window.CartService.count - res.carts.length;
      window.CartService.updateCount();
    })
    .catch((error = {}) => {
      modal.error(
        error.message
        ?? translator.t('main', 'An error occurred when deleting from the basket!'),
      );
    });
}

/**
 * Удаляет из корзины все билеты выбранной Услуги (service)
 * @param {number} eventId
 * @param {number} orderId
 * @param {number} countTickets
 */
function removeService(eventId, orderId, countTickets) {
  api.service
    .removeService({ serviceId: eventId, count: countTickets })
    .then((res) => {
      if (res.error) throw new Error(res.msg);
      if (res.refresh) return window.location.reload();
      const orderKey = orders.value.findIndex((order) => order.id === orderId);
      orders.value[orderKey].cases = orders.value[orderKey].cases.filter(
        (event) => event.id !== eventId,
      );
      /* удаляет заказ если нет мероприятии */
      if (!orders.value[orderKey].cases.length) orders.value.splice(orderKey, 1);
      // TODO: удалить после переноса хедера на Vue
      window.CartService.count = window.CartService.count - countTickets;
      window.CartService.updateCount();

      if (res.carts.length) {
        updateSummaryPrice(res.summ, res.carts[0].order_id);
      }
    })
    .catch((error = {}) => {
      modal.error(
        error.message
        ?? translator.t('main', 'An error occurred when deleting from the basket!'),
      );
    });
}

function saveExtraInfo(ticket, text) {
  api.eventCart
    .saveExtraInfo({ uid: ticket.uid, text })
    .then((res) => {
      if (res.error) throw new Error(res.msg);
      if (res.refresh) return window.location.reload();
      loadCart();
    })
    .catch((error = {}) => {
      modal.error(
        error.message
        ?? translator.t('main', 'An error occurred when deleting from the basket!'),
      );
    });
}

/**
 * @param {import("@api/types/cart").Ticket} ticket
 * @param {number} eventId
 */
function removeEventTicket(ticket, eventId) {
  api.eventCart
    .removeFromCart({ sid: ticket.sid, uid: ticket.uid, eventId })
    .then((res) => {
      if (res.error) throw new Error(res.msg);
      if (!Array.isArray(res.carts)) throw new Error('Не удалось получить удаленные билеты!');

      removeTicketMutation(res.order_id, eventId, ticket);
      updateOrderPrices(res);
      // TODO: удалить после переноса хедера на Vue
      window.CartService.count--;
      window.CartService.updateCount();
    })
    .catch((error) => {
      modal.error(
        error.message
        ?? translator.t('main', 'An error occurred when deleting from the basket!'),
      );
    });
}

/**
 * @param {import("@api/types/cart").Ticket} ticket
 * @param {number} eventId
 */
function removeServiceTicket(ticket, eventId) {
  api.service
    .removeTicket({ service_id: eventId, uid: ticket.uid })
    .then((res) => {
      if (res.error) throw new Error(res.msg);
      if (!Array.isArray(res.carts)) throw new Error('Не удалось получить удаленные билеты!');

      removeTicketMutation(res.order_id, eventId, ticket);
      updateOrderPrices(res);
      // TODO: удалить после переноса хедера на Vue
      window.CartService.count--;
      window.CartService.updateCount();
    })
    .catch((error) => {
      modal.error(
        error.message
        ?? translator.t('main', 'An error occurred when deleting from the basket!'),
      );
    });
}

function refundTickets(orderId, eventId) {
  api.refund
    .refund({ orderId, eventId })
    .then((res) => {
      if (res.error) throw new Error(res.msg);
      if (res.success) {
        loadHistory();
      } else {
        throw new Error(res.message);
      }
    })
    .catch((error) => {
      modal.error(error.message ?? 'Ошибка оформления возврата');
    });
}

function changeLoyalty(ticket, eventId, orderId, categoryId, priceId) {
  api.eventCart
    .changePriceId({
      eventId,
      orderId,
      uid: ticket.uid,
      priceId,
      categoryId,
    })
    .then((res) => {
      if (res.error) throw Error(res.msg);
      loadCart();
    })
    .catch((error) => {
      modal.error(error.message ?? 'Ошибка смены типа билета');
    });
}

function getOrderById(orderId) {
  return orders.value.find((order) => order.id === orderId);
}

function getTicketCountByOrderId(orderId) {
  const order = getOrderById(orderId);

  if (order?.cases) {
    return order.cases.reduce((accum, order) => accum + order.tickets.length, 0);
  }

  if (!order?.cases) {
    return 0;
  }

  let orderTicketsCount = 0;

  for (const c of order.cases) {
    orderTicketsCount += Object.values(c.events).reduce((accum, el) => accum + el.carts.length, 0);
  }

  return orderTicketsCount;
}

function getSubscriptionTicketCountByOrderId(orderId) {
  const order = getOrderById(orderId);

  let orderTicketsCount = 0;

  for (const c of order.cases) {
    orderTicketsCount += Object.values(c.events).reduce((accum, el) => accum + el.carts.length, 0);
  }

  return orderTicketsCount;
}

function removeOrderById(orderId) {
  api.order
    .cancel({ orderId })
    .then((res) => {
      if (res.error) throw new Error(res.msg);

      deleteOrderByIdFromStore(orderId);
    })
    .catch((err) => {
      modal.error(
        `
      ${translator.t('main', 'An error occurred when deleting from the basket!')}
      <p>${err.message}</p>
      `,
      );

      // TODO: подумать, нужно ли здесь обновлять страницу. Если нужно, то подумать над реализацией
      // modal.on("hide", () => window.location.reload());
    });
}

function removeEventFromSubscription(subscriptionEventObject, orderId) {
  const order = getOrderById(orderId);
  const ticketsCountBeforeRemove = getSubscriptionTicketCountByOrderId(orderId);
  for (const el of order.cases) {
    delete el.events[subscriptionEventObject.subEventId];
  }

  api.subscription
    .removeEventSids({
      eventId: subscriptionEventObject.eventId,
      subEventId: subscriptionEventObject.subEventId,
    })
    .then((res) => {
      if (res.result !== true) {
        throw new Error(res.msg);
      }

      loadCart().then((res) => {
        if (res.length) {
          const ticketsCountAfterRemove = getSubscriptionTicketCountByOrderId(orderId);
          const ticketsCountDifference = ticketsCountBeforeRemove - ticketsCountAfterRemove;

          // TODO: удалить после переноса хедера на Vue
          window.CartService.count = window.CartService.count - ticketsCountDifference;
          window.CartService.updateCount();

          return;
        }

        // TODO: удалить после переноса хедера на Vue
        window.CartService.count = 0;
        window.CartService.updateCount();
      });
    })
    .catch((err) => {
      modal.error(err.message ?? 'Ошибка удаления мероприятия');
    });
}

export default function useCartStore() {
  return {
    getCart: readonly(orders),
    getHistory: readonly(history),
    getcurrentOpenedEventId: readonly(currentOpenedEventId),
    isShowFastBuyModal,
    eventTypeToPay,
    setFastBuyModalStatus,
    setEventTypeToPay,
    setCurrentOpenedEventId,
    loadHistory,
    loadCart,
    clearCart,
    removeEvent,
    removeEventTicket,
    removeServiceTicket,
    removeService,
    updateOrderPrices,
    refundTickets,
    saveExtraInfo,
    changeLoyalty,
    removeOrderById,
    removeEventFromSubscription,
    updateOrderSale,
    deleteOrderByIdFromStore,
  };
}
